export type CellPosition = { col: number; row: number };

/**
 * Converts a number to an Excel column name
 *
 * Example:
 *
 * toExcelColumnName(1) -> A
 * toExcelColumnName(27) -> AA
 */
export function toExcelColumnName(n: number): string {
  let s = '';
  while (n > 0) {
    s = String.fromCharCode(((n - 1) % 26) + 65) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/**
 * Converts an Excel column name to a number
 *
 * Example:
 *
 * toExcelColumnNumber('A') -> 1
 * toExcelColumnNumber('AA') -> 27
 */
export function toExcelColumnNumber(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    n = n * 26 + s.charCodeAt(i) - 64;
  }
  return n;
}

/**
 * Converts a column number and row number to a cell reference
 *
 * Example:
 *
 * getCellRef(1, 3) -> A3
 */
export function getCellRef(col: number, row: number) {
  return `${toExcelColumnName(col)}${row}`;
}

/**
 * Converts a cell reference to a column number and row number
 *
 * Example:
 *
 * getCellPos('A3') -> { col: 1, row: 3 }
 * getCellPos('AB12') -> { col: 28, row: 12 }
 */
export function getCellPos(ref: string): CellPosition {
  const colStr = ref.match(/[A-Z]+/)?.[0];
  const rowStr = ref.match(/\d+/)?.[0];
  if (!colStr || !rowStr) throw new Error('Invalid cell reference');
  return {
    col: toExcelColumnNumber(colStr),
    row: Number(rowStr),
  };
}

/**
 * Parses a cell reference. Refences can be of these formats:
 * - A1
 * - $A$1
 * - $A1
 * - A$1
 */
export function parseRef(ref: string): CellPosition {
  return getCellPos(ref.replaceAll('$', ''));
}

export type CellRange = {
  start: CellPosition;
  end: CellPosition;
};

/**
 * Converts a cell range reference to a start and end position
 *
 * Example:
 *
 * getCellRange('A1:C3') -> { start: { col: 1, row: 1 }, end: { col: 3, row: 3 } }
 */
export function parseRefRange(ref: string): CellRange {
  const [startRef, endRef] = ref.split(':');
  if (!startRef || !endRef) throw new Error('Invalid cell range');
  return {
    start: parseRef(startRef),
    end: parseRef(endRef),
  };
}
